import { useMediaQuery } from '@material-ui/core';
import { Agent, CheckoutSession, getAgentById, PhoneNumber, postDistributeHppByHppId } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { ActionDialog } from '@/components/shared/components/ActionDialog';
import {
  DISTRIBUTION_AVAILABLE_COUNTRIES_ISO2,
  DistributionForm,
} from '@/components/shared/components/DistributionDialog/components/DistributionForm';
import { DefaultDistributionForm } from '@/components/shared/components/DistributionDialog/constants/DefaultDistributionForm';
import { MAX_ALLOWED_DISTRIBUTIONS } from '@/components/shared/components/DistributionDialog/constants/MaxAllowedDistributions';
import { DistributionFormSchema } from '@/components/shared/components/DistributionDialog/schemas/DistributionFormSchema';
import { DistributionMethod } from '@/components/shared/components/DistributionDialog/types/DistributionMethod';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { EmptyDialCodeOption } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/constants/EmptyDialCodeOption';
import { getDialCodeItemFromList } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/getDialCodeItemFromList';
import { Text } from '@/components/shared/components/Text';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useGetCheckoutSessionByHppId } from '@/hooks/requests/checkoutSession/useGetCheckoutSessionByHppId';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface DistributionDialogProps {
  hppId: string;
  agentId: string;
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
  checkoutSession?: CheckoutSession;
  openTo?: DistributionMethod;
  beforeSubmit?: () => Promise<void>;
}

type DistributionFormFields = z.infer<ReturnType<typeof DistributionFormSchema>>;

export const DistributionDialog = (props: DistributionDialogProps) => {
  const { data: agent } = useCache(getAgentById, { agentId: props.agentId });

  if (!agent) {
    return null;
  }

  return <DistributionDialogWrapper {...props} agent={agent} />;
};

interface DistributionDialogWrapperProps extends DistributionDialogProps {
  agent: Agent;
}

const DistributionDialogWrapper = ({ agent, ...props }: DistributionDialogWrapperProps) => {
  const { checkoutSession: checkoutSessionByHppId } = useGetCheckoutSessionByHppId(agent, props.hppId);

  const checkoutSession = checkoutSessionByHppId || props.checkoutSession;

  if (!checkoutSession) {
    return null;
  }

  const phoneNumber = checkoutSession.customer?.phoneNumber
    ? new PhoneNumber(checkoutSession.customer.phoneNumber)
    : null;

  const defaultValues = {
    ...DefaultDistributionForm,
    email: checkoutSession.customer?.email || DefaultDistributionForm.email,
    dialCode: phoneNumber?.countryCallingCode
      ? getDialCodeItemFromList(phoneNumber.countryCallingCode, DISTRIBUTION_AVAILABLE_COUNTRIES_ISO2) ??
        EmptyDialCodeOption
      : DefaultDistributionForm.dialCode,
    phoneNumber: phoneNumber?.nationalNumber || DefaultDistributionForm.phoneNumber,
  };

  return (
    <FormWrapper formSchema={DistributionFormSchema} defaultValues={defaultValues}>
      <DistributionDialogInner {...props} agent={agent} />
    </FormWrapper>
  );
};

interface DistributionDialogInnerProps extends DistributionDialogWrapperProps {
  agent: Agent;
}

const DistributionDialogInner = ({
  hppId,
  opened,
  setOpened,
  openTo,
  beforeSubmit,
  checkoutSession,
  agent,
}: DistributionDialogInnerProps) => {
  const {
    state: { hasActiveDistributionMethods },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const { request, loading } = useRequest();
  const { handleSubmit } = useFormContext();
  const { revalidate } = useGetCheckoutSessionByHppId(agent, hppId);

  const { breakpoints } = useCustomTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const [distributionMethod, setDistributionMethod] = useState(openTo ?? DistributionMethod.SEND_LINK);

  if (!checkoutSession) return null;

  const submitDistribution = async (recipient) => {
    await request(postDistributeHppByHppId, {
      hppId,
      recipient,
    });
  };

  const onSubmit = async (data: DistributionFormFields) => {
    try {
      if (beforeSubmit) {
        await beforeSubmit();
      }
      if (data.activeMethods.sms) {
        await submitDistribution(`+${data.dialCode!.dialCode}${data.phoneNumber}`);
      }
      if (data.activeMethods.email) {
        await submitDistribution(data.email);
      }
      if (revalidate) {
        await revalidate();
      }
      updateApplicationContext({ pollingCounter: 1 });
    } catch (error) {
    } finally {
      setOpened(false);
    }
  };

  const handleClose = () => {
    setOpened(false);
  };

  const maxDistributionsReached =
    checkoutSession.hpp?.distributions && checkoutSession.hpp.distributions.length >= MAX_ALLOWED_DISTRIBUTIONS;

  return (
    <ActionDialog
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      Title={<Text tId={'SHARE_PAYMENT_LINK'} />}
      Description={<Text tId={'SHARE_PAYMENT_LINK_DESCRIPTION'} />}
      onSubmit={handleSubmit(onSubmit)}
      MainButton={<Text tId={'SEND'} />}
      hideMainButton={distributionMethod !== DistributionMethod.SEND_LINK || maxDistributionsReached}
      loading={loading}
      disabled={!hasActiveDistributionMethods}
      dialogProps={{
        maxWidth: 'sm',
        ...(!isMobile && { style: { top: '100px', bottom: 'inherit' } }),
      }}
      avoidDefaultClose={false}
    >
      <DistributionForm
        distributionMethod={distributionMethod}
        setDistributionMethod={setDistributionMethod}
        checkoutSession={checkoutSession}
      />
    </ActionDialog>
  );
};
